import { Module } from './../model/module';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, signal, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule as formsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ComponentBase } from '../../../../common/component-base';
import { ModuleService } from '../module.service';
import { NotificationService } from '../../../../common/service/notification.service';
import { Table, TableFilterEvent, TableModule, TablePageEvent } from 'primeng/table';
import { environment } from '../../../../environment/environment';
import { LoaderComponent } from '../../../../common/component/loader/loader.component';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { TranslationService } from '../../../../common/service/translate.service';
import { Claim } from '../model/claim';
import { MultiSelect } from 'primeng/multiselect';
import { Checkbox } from 'primeng/checkbox';
import { TableFilterIconComponent } from '../../../../common/component/table-filter-icon/table-filter-icon.component';
import { clearTableSessionState, getTableSearchValue, getTableSessionState, persistTableSessionStatePatch, setTableSearchValue, TablePersistenceConfig } from '../../../../common/helper/table-session-state';
import { canRunTableSearch, normalizeTableSearchValue, PrimeNgTableSearch } from '../../../../common/helper/primeng-table-search';
import { TablePersistenceDirective } from '../../../../common/directive/table-persistence.directive';

@Component({
  selector: 'gate-module-list',
  standalone: true,
  imports: [formsModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TranslatePipe,
    TableModule,
    NgClass,
    LoaderComponent,
    ConfirmDialog,
    ToggleSwitch,
    MultiSelect,
    Checkbox,
    TableFilterIconComponent,
    TablePersistenceDirective,
  ],
  templateUrl: './module-list.component.html',
  styleUrl: './module-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class ModuleListComponent extends ComponentBase implements OnDestroy {
  private readonly tableStateKey = 'moduleListTableState';
  private isApplyingRestoredPage = false;
  private tablePageRestoreTimeouts: ReturnType<typeof setTimeout>[] = [];
  private isUserPaginatorInteraction = false;
  private removePaginatorClickListener: (() => void) | undefined;
  private removePaginatorKeydownListener: (() => void) | undefined;

  // State variables
  public modules = signal<Module[]>([]); // Holds the list of modules
  @Input() claims = signal<Claim[]>([]); // Holds the list of claims
  public displayDialog = false; // Controls visibility of modal/dialog
  public isAdding = signal(false); // Tracks if a module is being added
  public itemsPerPage = environment.DefaultItemsPerPage; // Default pagination size
  public tableFirst = getTableSessionState(this.tableStateKey)?.first ?? 0;
  public tableRows = getTableSessionState(this.tableStateKey)?.rows ?? environment.DefaultItemsPerPage;
  confirmDefaultTitle = signal('');
  confirmDefaultMessage = signal('');
  confirmDeleteTitle = signal('');
  confirmDeleteMessage = signal('');
  confirmPublicTitle = signal('');
  confirmTogglePublicMessage = signal('');
  searchString = signal('');
  private searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private hasColumnFilter = false;
  @ViewChild('moduleMultiSelect') moduleMultiSelect!: MultiSelect;
  @ViewChild('moduleTable') moduleTable!: Table;

  constructor(
    private moduleService: ModuleService,
    private confirmationService: ConfirmationService,
    private notificationService: NotificationService,
    private translationService: TranslationService,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>
  ) {
    super();
    this.searchString.set(getTableSearchValue(this.tableStateKey));
    this.itemsPerPage = this.tableRows;
    this.getTranslate();
    this.translate.onLangChange.subscribe((e) => {
      this.getTranslate();
      this.translateClaimNames();
    });
    this.loadModules();
    this.moduleService.newModuleAdded.subscribe((module: Module) => {
      module = this.translateClaim(module);
      const newModule = { ...module, isTempDefault: module.isDefault, isTempPublic: module.isPublic, tempClaims: module.claims, claimString: this.getAssignedClaims(module) };
      this.modules.update((mods) => [newModule, ...mods]);
    });
  }

  ngOnDestroy(): void {
    this.tablePageRestoreTimeouts.forEach((timeout) => clearTimeout(timeout));
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.removePaginatorClickListener?.();
    this.removePaginatorKeydownListener?.();
  }

  private getTranslate() {
    const confirmDefaultTitle = 'kam.moduleManagement.confirmSetDefaultHeader';
    const confirmDefaultMessage = 'kam.moduleManagement.confirmSetDefault';
    const confirmDeleteTitle = 'kam.moduleManagement.confirmDeleteHeader';
    const confirmDeleteMessage = 'kam.moduleManagement.confirmDelete';
    const confirmPublicTitle = 'kam.moduleManagement.confirmSetPublicHeader';
    const confirmTogglePublicMessage = 'kam.moduleManagement.confirmTogglePublic';
    // Initial translation
    this.translationService.translateKeys([confirmDefaultTitle, confirmDefaultMessage, confirmDeleteTitle, confirmDeleteMessage, confirmPublicTitle, confirmTogglePublicMessage], this.translationService).then((translations) => {
      this.confirmDefaultTitle.set(translations[confirmDefaultTitle]);
      this.confirmDefaultMessage.set(translations[confirmDefaultMessage]);
      this.confirmDeleteTitle.set(translations[confirmDeleteTitle]);
      this.confirmDeleteMessage.set(translations[confirmDeleteMessage]);
      this.confirmPublicTitle.set(translations[confirmPublicTitle]);
      this.confirmTogglePublicMessage.set(translations[confirmTogglePublicMessage]);
    });
  }

  /**
   * Loads all modules from the backend service.
   * Clears the current list, fetches modules,
   * initializes temporary default and public state, and updates the signal.
   */
  private loadModules() {
    this.SetLoading(true);
    this.modules.set([]); // Clear existing
    this.moduleService
      .getAllModules()
      .then((mods) => {
        // Initialize isTempDefault and isTempPublic to track UI toggle separately
        mods.forEach(m => {
          m.claims?.forEach(claim => {
            claim.translatedName = this.translate.instant(`userManagement.roles.${claim.roleName?.toLocaleLowerCase()}`);
          });
        });
        mods.map((m) => {
          m.isTempDefault = m.isDefault;
          m.isTempPublic = m.isPublic;
          m.claimString = this.getAssignedClaims(m);
        });
        this.modules.set(mods);
        this.restoreTablePageForCurrentResult();
        this.applyRestoredSearch();
      })
      .catch((error) => console.error('Error fetching modules:', error))
      .finally(() => this.SetLoading(false));
  }

  /**
   * Opens the module management dialog.
   */
  openDialog() {
    this.displayDialog = true;
    this.modules.update((mods) =>
      // reset both temporary flags when opening dialog
      mods.map((m) => ({ ...m, isTempDefault: m.isDefault, isTempPublic: m.isPublic }))
    );
  }

  /**
   * Shows a confirmation dialog before deleting a module.
   * @param module Module object to be deleted
   */
  public confirmDelete(module: Module) {
    this.confirmationService.confirm({
      header: this.confirmDeleteTitle(),
      message: this.confirmDeleteMessage(),
      rejectButtonProps: { severity: 'contrast' },
      accept: () => this.deleteModule(module.moduleId),
    });
  }

  /**
   * Deletes a module via API and updates UI state accordingly.
   * @param moduleId ID of the module to delete
   */
  private deleteModule(moduleId: number) {
    // Set module as "deleting" for UI feedback
    this.modules.update((mods) =>
      mods.map((m) => (m.moduleId == moduleId ? { ...m, isDeleting: true } : m))
    );

    this.moduleService
      .deleteModule(moduleId)
      .then(() => {
        // Remove deleted module from the list
        this.modules.update((mods) => mods.filter((m) => m.moduleId != moduleId));
        this.notificationService.showNotification(
          'success',
          'notification.moduleManagement.delete.success.title',
          'notification.moduleManagement.delete.success.message'
        );
      })
      .catch((error) => {
        this.notificationService.showNotification(
          'error',
          'notification.moduleManagement.error.title',
          'notification.moduleManagement.error.message'
        );
        console.error('Error deleting module:', error);
      })
      .finally(() => {
        // Reset deleting state
        this.modules.update((mods) =>
          mods.map((m) => (m.moduleId == moduleId ? { ...m, isDeleting: false } : m))
        );
      });
  }

  /**
   * Handles default module toggling.
   * - If already default, resets state
   * - If not, asks for confirmation before setting as default
   */
  public toggleDefault(module: Module) {
    this.confirmationService.confirm({
      header: this.confirmDefaultTitle(),
      message: this.confirmDefaultMessage(),
      rejectButtonProps: { severity: 'contrast' },
      accept: () => this.setDefault(module),
      reject: () => {
        this.resetModule(module); // Restore previous state
      },
    });
  }

  /**
   * Restores the temporary state of a module to its actual saved default state.
   * @param module Module to reset
   */
  private resetModule(module: Module) {
    this.modules.update((mods) =>
      mods.map((m) =>
        m.moduleId == module.moduleId
          ? { ...m, isTempDefault: m.isDefault }
          : m,
      ),
    );
  }
  /**
   * Restores the temporary state of a module to its actual saved public state.
   * @param module Module to reset
   */
  private resetModulePublicValue(module: Module) {
    this.modules.update((mods) =>
      mods.map((m) =>
        m.moduleId == module.moduleId
          ? { ...m, isTempPublic: m.isPublic }
          : m,
      ),
    );
  }

  /**
   * Toggle the temporary public flag for a module (UI only).
   * Can freely toggle between true/false states.
   * @param module Module to toggle public state
   */
  public togglePublic(module: Module) {
    this.confirmationService.confirm({
      header: this.confirmPublicTitle(),
      message: this.confirmTogglePublicMessage(),
      rejectButtonProps: { severity: 'contrast' },
      accept: () => {
        this.setPublic(module);
      },
      reject: () => {
        module = {...module, isPublic: !module.isPublic };
        this.resetModulePublicValue(module); // Restore previous state
      },
    });
  }


  onHideDialog() {
    this.displayDialog = false;
    this.modules.update((mods) =>
      mods.map((m) => ({ ...m, isTempDefault: m.isDefault, isTempPublic: m.isPublic }))
    );
  }
  /**
   * Sets a module as default via API.
   * - Updates backend
   * - On success, calls toggleDefaultConfirmed to update UI state
   * @param module Module to set as default
   */
  private setDefault(module: Module) {
    this.modules.update((mods) =>
      mods.map((m) => (m.moduleId == module.moduleId ? { ...m, isDefaultLoading: true } : m))
    );
    this.moduleService
      .setDefaultModule(module.moduleId)
      .then(() => {
        this.notificationService.showNotification(
          'success',
          'notification.moduleManagement.setDefault.success.title',
          'notification.moduleManagement.setDefault.success.message'
        );
        this.toggleDefaultConfirmed(module);
      })
      .catch((error) => {
        this.resetModule(module); // Revert state if error
        this.notificationService.showNotification(
          'error',
          'notification.moduleManagement.error.title',
          'notification.moduleManagement.error.message'
        );
        console.error('Error setting default module:', error);
      });
  }


  /**
   * Sets a public state of module via API.
   * - Updates backend
   * - On success, updates only that module's public state (others remain unchanged)
   * @param module Module to update public state
   */
  private setPublic(module: Module) {
    this.modules.update((mods) =>
      mods.map((m) => (m.moduleId == module.moduleId ? { ...m, isPublicLoading: true } : m))
    );
    this.moduleService
      .setPublicModule(module.moduleId, module.isTempPublic!)
      .then(() => {
        this.notificationService.showNotification(
          'success',
          'notification.moduleManagement.setPublic.success.title',
          'notification.moduleManagement.setPublic.success.message'
        );
        this.togglePublicConfirmed(module);
      })
      .catch((error) => {
        this.resetModulePublicValue(module); // Revert state if error
        this.notificationService.showNotification(
          'error',
          'notification.moduleManagement.error.title',
          'notification.moduleManagement.error.message'
        );
        console.error('Error setting public module:', error);
      });
  }

  /**
   * Updates local state after confirming a default module change.
   * Ensures only one module is default at a time.
   * @param module Module confirmed as default
   */
private toggleDefaultConfirmed(module: Module) {
  this.modules.update((mods) =>
    mods.map((m) => {
      if (m.moduleId == module.moduleId) {
        return { ...m, isDefault: !module.isDefault, isTempDefault: !module.isDefault, isDefaultLoading: false };
      }

      // Always return the original item when not matched
      return m;
    })
  );
}

  /**
   * Updates local state after confirming a public module change.
   * Ensures only one module is default at a time.
   * @param module Module confirmed as default
   */
  private togglePublicConfirmed(module: Module) {
    this.modules.update((mods) =>
      mods.map((m) => {
        if (m.moduleId === module.moduleId) {
          return { ...m, isTempPublic: module.isTempPublic, isPublicLoading: false};
        } else {
          return { ...m};
        }
      })
    );
  }

  public getAssignedClaims(module: Module): string {
    return module.claims?.map(claim => claim.translatedName)?.join(' | ') || '';
  }

  public clearDataTable(table: Table): void {
    clearTableSessionState(this.tableStateKey);
    this.tableFirst = 0;
    this.tableRows = environment.DefaultItemsPerPage;
    this.itemsPerPage = environment.DefaultItemsPerPage;
    table.clear();
    this.searchString.set('');
    this.hasColumnFilter = false;
  }

  // Whether the search box or any column filter currently has a value - drives the
  // Clear Filter button's visibility.
  public hasActiveFilterOrSearch(): boolean {
    return !!this.searchString().trim() || this.hasColumnFilter;
  }

  public onFilter(event: TableFilterEvent): void {
    this.hasColumnFilter = Object.entries(event.filters ?? {}).some(([field, meta]) => {
      if (field === 'global') {
        return false;
      }
      const value = Array.isArray(meta) ? meta[0]?.value : (meta as { value?: unknown })?.value;
      return value !== null && value !== undefined && value !== '';
    });
  }

  public onPageChange(event: TablePageEvent): void {
    if (this.isApplyingRestoredPage) {
      return;
    }

    const savedFirst = getTableSessionState(this.tableStateKey)?.first ?? 0;

    if (event.first === 0 && this.tableFirst > 0 && savedFirst > 0 && !this.isUserPaginatorInteraction) {
      this.restoreTablePageForCurrentResult();
      return;
    }

    this.tableFirst = event.first;
    this.tableRows = event.rows;
    this.itemsPerPage = this.tableRows;
    persistTableSessionStatePatch(this.tableStateKey, {
      first: event.first,
      rows: event.rows,
    });
    this.isUserPaginatorInteraction = false;
  }

  public onSearchChange(value: string, table: Table): void {
    const searchValue = normalizeTableSearchValue(value);
    this.searchString.set(value);
    setTableSearchValue(this.tableStateKey, searchValue);

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.searchDebounceTimer = setTimeout(() => {
      if (!canRunTableSearch(searchValue)) {
        return;
      }

      this.resetTablePageToFirst(table);
      table.filterGlobal(searchValue, 'contains');
    }, PrimeNgTableSearch.debounceMs);
  }

  private resetTablePageToFirst(table: Table): void {
    this.tableFirst = 0;
    table.first = 0;
    table.firstChange.emit(0);
    persistTableSessionStatePatch(this.tableStateKey, {
      first: 0,
      rows: this.tableRows,
    });
    this.changeDetector.detectChanges();
  }

  private applyRestoredSearch(): void {
    const searchValue = this.searchString().trim();

    if (canRunTableSearch(searchValue) && searchValue) {
      const restoredState = getTableSessionState(this.tableStateKey);
      setTimeout(() => {
        this.moduleTable?.filterGlobal(searchValue, 'contains');
        this.restoreTablePageForCurrentResult(restoredState);
      });
    }
  }

  private restoreTablePageForCurrentResult(restoredState = getTableSessionState(this.tableStateKey)): void {
    const state = restoredState;
    const rows = state?.rows ?? this.tableRows;
    const first = state?.first ?? this.tableFirst;
    const totalRecords = this.modules().length;
    const validFirst = totalRecords > 0 && first >= totalRecords
      ? Math.max(Math.floor((totalRecords - 1) / rows) * rows, 0)
      : first;

    this.tableRows = rows;
    this.itemsPerPage = rows;
    this.tableFirst = validFirst;
    this.applyRestoredPageToPrimeTable(validFirst, rows);
  }

  private applyRestoredPageToPrimeTable(first: number, rows: number): void {
    this.bindPaginatorInteractionListeners();
    this.tablePageRestoreTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.tablePageRestoreTimeouts = [0, 50, 250, 500, 1000, 1600, 2200].map((delay) =>
      setTimeout(() => {
        const table = this.moduleTable;

        if (!table) {
          return;
        }

        this.isApplyingRestoredPage = true;
        table.onPageChange({
          first,
          rows,
          page: Math.floor(first / rows),
          pageCount: Math.ceil(this.modules().length / rows),
        } as TablePageEvent);
        table.first = first;
        table.rows = rows;
        this.tableFirst = first;
        this.tableRows = rows;
        this.itemsPerPage = rows;
        persistTableSessionStatePatch(this.tableStateKey, { first, rows });
        this.changeDetector.detectChanges();

        setTimeout(() => {
          this.isApplyingRestoredPage = false;
        });
      }, delay),
    );
  }

  private bindPaginatorInteractionListeners(): void {
    if (this.removePaginatorClickListener || this.removePaginatorKeydownListener) {
      return;
    }

    const nativeElement = this.elementRef.nativeElement;
    const markPaginatorInteraction = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.p-paginator')) {
        this.isUserPaginatorInteraction = true;
      }
    };

    nativeElement.addEventListener('click', markPaginatorInteraction, true);
    nativeElement.addEventListener('keydown', markPaginatorInteraction, true);

    this.removePaginatorClickListener = () => nativeElement.removeEventListener('click', markPaginatorInteraction, true);
    this.removePaginatorKeydownListener = () => nativeElement.removeEventListener('keydown', markPaginatorInteraction, true);
  }

  private translateClaimNames() {
    this.modules.update((modules) => {
      return modules.map((module) => {
        return this.translateClaim(module);
      });
    });
  }

  private translateClaim(module: Module) {
    const updatedModule = { ...module };
    updatedModule.claims = module.claims?.map((claim) => ({
      ...claim,
      translatedName: this.translate.instant(`userManagement.roles.${claim.roleName?.toLocaleLowerCase()}`),
    }));
    updatedModule.tempClaims = module.tempClaims?.map((claim) => ({
      ...claim,
      translatedName: this.translate.instant(`userManagement.roles.${claim.roleName?.toLocaleLowerCase()}`),
    }));
    updatedModule.claimString = this.getAssignedClaims(updatedModule);
    return updatedModule;
  }

  public areClaimsEqual(claimsA: Claim[], claimsB: Claim[]): boolean {
    if (!claimsA || !claimsB) return false;
    if (claimsA.length != claimsB.length) return false;

    // Sort by id to avoid order issues
    const sortedA = [...claimsA].sort((a, b) => a.roleId - b.roleId);
    const sortedB = [...claimsB].sort((a, b) => a.roleId - b.roleId);

    return sortedA.every((mod, index) =>
      mod.roleId == sortedB[index].roleId
    );
  }

  public onAccessUpdate(module: Module, onCancel: boolean = false) {
    if (onCancel) {
      module.editing = false;
      return;
    }
    const modules = this.modules();
    const index = modules.findIndex((o) => o.moduleId == module.moduleId);
    if (index != -1) {
      const updatedModule = { ...modules[index], editing: false, claims: !onCancel ? modules[index].tempClaims : modules[index].claims, tempClaims: modules[index].claims, claimString: this.getAssignedClaims(modules[index]) };
      const updatedModules = [...modules];
       updatedModules[index] = this.translateClaim(updatedModule);
      this.modules.set(updatedModules); // Update the signal
      this.updateAccess(updatedModules[index])
    }
  }

  private updateAccess(module: Module) {
    this.setClaimsUpdating(module.moduleId, true);
    this.moduleService
      .updateModuleClaims(module.moduleId, module.claims || [])
      .then((result: any) => {
        this.setClaimsUpdating(module.moduleId, false);
        this.notificationService.showNotification('success', 'notification.moduleManagement.updateAccess.success.title', 'notification.moduleManagement.updateAccess.success.message');
      })
      .catch((error) => {
        this.setClaimsUpdating(module.moduleId, false);
        console.error('Error fetching serviceReports:', error); // Log error to console
        this.notificationService.showNotification('error', 'notification.moduleManagement.updateAccess.error.title', 'notification.moduleManagement.updateAccess.error.message');
      });
  }

  private setClaimsUpdating(moduleId: number, isClaimsUpdating: boolean): void {
    this.modules.update((mods) =>
      mods.map((m) => (m.moduleId === moduleId ? { ...m, isClaimsUpdating } : m))
    );
  }
  public enableEditing(module: Module) {
    module.editing = true;
    module.tempClaims = module.claims
    this.changeDetector.detectChanges();
    this.moduleMultiSelect?.show();
  }
}
